import HeadingWithShortDescription from "components/Heading/HeadingWithShortDescription";
import React, { useState } from "react";
import NotificationContext from "views/Components/Context.js";
import {
    Box,
    CircularProgress,
    Container, createMuiTheme,
    Divider,
    ExpansionPanel,
    ExpansionPanelSummary,
    ExpansionPanelDetails,
    FormControlLabel,
    Grid,
    IconButton,
    Slider,
    Switch,
    Tooltip,
    ThemeProvider,
    Typography,

} from '@material-ui/core';
import { Visibility, Save, ExpandMore } from '@material-ui/icons';
import { Collapse } from '@material-ui/core';
import Button from "components/CustomButtons/Button";

const DataIngestionStep = () => {
    const project = {
        name: "Data Project 1",
        description: "Data Project Description 1",
    };

    const files = [
        { id: '1', name: 'introduction.txt' },
        { id: '2', name: 'chapter1.txt' },
        { id: '3', name: 'conclusion.txt' },
    ];

    const [rows, setRows] = useState(
        files.map((f) => ({
            ...f,
            content: '',
            dirty: false,
            saving: false,

            summarize: false,
            qaPairs: false,
            qaCount: 5,
            cotPairs: false,
            cotCount: 5,
        }))
    );
    const [viewer, setViewer] = useState({ open: false, title: '', body: '' });
    const { showNotification } = React.useContext(NotificationContext);

    const [busy, setBusy] = useState(false);   // global “Generate” busy
    const updateRow = (id, patch) =>
        setRows((rs) =>
            rs.map((r) => (r.id === id ? { ...r, ...patch } : r))
        );

    /* -------------------------------------------------------- */
    /* view file                                                 */
    /* -------------------------------------------------------- */
    const handleView = async (row) => {
        try {
            if (!row.content) {
                // const body = await fetchContentApi(row.id);
                updateRow(row.id, { content: "body" });
                setViewer({ open: true, title: row.name, body: "body" });
            } else {
                setViewer({ open: true, title: row.name, body: row.content });
            }
        } catch (err) {
            showNotification({ open: true, msg: `Failed to load ${row.name}`, type: 'error' });
        }
    };

    const handleSave = async (row) => {
        updateRow(row.id, { saving: true });
        try {
            //   await saveFileApi(row.id, row.content);
            updateRow(row.id, { saving: false, dirty: false });
            showNotification(`Saved ${row.name}`, 'success');
        } catch (err) {
            updateRow(row.id, { saving: false });
            showNotification(`Save failed for ${row.name}`, 'error');
        }
    };


    const handleGenerate = async () => {
        setBusy(true);

        /* 1. save any dirty rows in parallel */
        try {
            const dirtyRows = rows.filter((r) => r.dirty);
            // await Promise.all(
            //     dirtyRows.map((r) => saveFileApi(r.id, r.content))
            // );
        } catch (err) {
            showNotification('Save failed - generation aborted', 'error');
            setBusy(false);
            return;
        }

        /* 2. build payload */
        const payload = rows.map(
            ({
                id,
                summarize,
                qaPairs,
                qaCount,
                cotPairs,
                cotCount,
            }) => ({
                id,
                summarize,
                qaPairs,
                qaCount,
                cotPairs,
                cotCount,
            })
        );

        /* 3. generate */
        try {
            //   await generateApi(payload);
            showNotification('Request submitted for generation', 'success');
        } catch (err) {
            showNotification('Generation failed', 'error');
        } finally {
            setBusy(false);
        }
    };


    const SliderWithLabel = ({ value, onChange }) => (
        <Box pt={1}>
            <Typography variant="caption">
                Questions&nbsp;:&nbsp;<strong>{value}</strong>
            </Typography>
            <Slider
                value={value}
                onChange={onChange}
                min={5}
                max={50}
                step={1}
                color="primary"
                valueLabelDisplay="auto"
                marks={[{ value: 5, label: 5 }, { value: 50, label: 50 }]}
            />
        </Box>
    );

    const theme = createMuiTheme({
        palette: {
            primary: { main: '#20677b' },           // sliders, switches, buttons
            text: { primary: '#000' },           // all Typography defaults to black
        },
    });

    return (
        <ThemeProvider theme={theme}>
            {/* heading */}
            <HeadingWithShortDescription
                title={project.name}
                shortDescription={project.description}
                dialogTitle="Edit Project Details"
            // onChange={handleUpdateLabDetails}
            />
            <Divider style={{
                marginTop: "-20px"
            }} />

            {/* section intro */}
            <Box mt={2} mb={3}>
                <Typography variant="h5">Data Ingestion</Typography>
                <Typography variant="body1">
                    Review the ingested data from the uploaded resources. Select the configurations for synthetic
                    data generation.
                </Typography>
            </Box>

            {/* panels */}
            {rows.map((row) => (
                <ExpansionPanel key={row.id} TransitionProps={{ unmountOnExit: true }}>
                    <ExpansionPanelSummary expandIcon={<ExpandMore />}>
                        <Typography variant="subtitle1">{row.name}</Typography>
                    </ExpansionPanelSummary>

                    <Divider />

                    <ExpansionPanelDetails>
                        <Grid container spacing={4}>
                            {/* left column – file text always visible */}
                            <Grid item xs={12} md={6}>
                                <Typography
                                    component="pre"
                                    style={{
                                        whiteSpace: 'pre-wrap',
                                        background: '#f7f7f7',
                                        padding: 12,
                                        borderRadius: 4,
                                        maxHeight: 250,
                                        overflowY: 'auto',
                                    }}
                                >
                                    {row.content || '— no content loaded —'}
                                </Typography>
                            </Grid>

                            {/* right column – vertical stack of controls */}
                            <Grid item xs={12} md={6}>
                                {/* Summarize */}
                                <FormControlLabel
                                    control={
                                        <Switch
                                            color="primary"
                                            checked={row.summarize}
                                            onChange={(e) => updateRow(row.id, { summarize: e.target.checked })}
                                        />
                                    }
                                    label="Summarize"
                                />

                                {/* QA pairs */}
                                <Box >
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                color="primary"
                                                checked={row.qaPairs}
                                                onChange={(e) => updateRow(row.id, { qaPairs: e.target.checked })}
                                            />
                                        }
                                        label="Generate QA Pairs"
                                    />
                                    <Collapse in={row.qaPairs}>
                                        <SliderWithLabel
                                            value={row.qaCount}
                                            onChange={(e, v) => updateRow(row.id, { qaCount: v })}
                                        />
                                    </Collapse>
                                </Box>

                                {/* COT pairs */}
                                <Box >
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                color="primary"
                                                checked={row.cotPairs}
                                                onChange={(e) => updateRow(row.id, { cotPairs: e.target.checked })}
                                            />
                                        }
                                        label="Generate COT QA Pairs"
                                    />
                                    <Collapse in={row.cotPairs}>
                                        <SliderWithLabel
                                            value={row.cotCount}
                                            onChange={(e, v) => updateRow(row.id, { cotCount: v })}
                                        />
                                    </Collapse>
                                </Box>

                                {/* save button */}
                                <Box mt={3} textAlign="right">
                                    <Button
                                        variant="contained"
                                        color="quCreateColor"
                                        onClick={() => handleSave(row)}
                                        disabled={row.saving}
                                    >
                                        {row.saving ? <CircularProgress size={20} /> : 'Save'}
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </ExpansionPanelDetails>
                </ExpansionPanel>
            ))}

            {/* footer action */}
            <Box mt={4} textAlign="right">
                <Button
                    variant="contained"
                    color="quCreateColor"
                    onClick={handleGenerate}
                    disabled={busy}
                    startIcon={busy && <CircularProgress size={20} />}
                >
                    {busy ? 'Processing…' : 'Generate Data'}
                </Button>
            </Box>
        </ThemeProvider>
    );
};

export default DataIngestionStep;